// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import BulletGrid from '@/components/sections/BulletGrid'
import { GlobalContext } from 'context/GlobalContextProvider'

// Img - reused from the AIR-1000 product page
import ActionImage from 'public/img/products/Air1000/air_quality_event.png'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBody } from '@/assets/theme/fonts'

export default function ActionSection() {
  const { t } = useTranslation('solutionsIotAirQualityMonitoring')
  const { screenDimensions } = useContext(GlobalContext)

  const w = screenDimensions.width
  const stacked = w < breakpoints.desktopToMobile

  /**
   * The photo keeps its own proportions, so the wider its column the taller it
   * renders - which is what closes the gap beside the copy on large screens.
   */
  const getGrid = () => {
    if (stacked) return '1fr'
    if (w > breakpoints.desktopLarge) return '1fr 1fr'
    if (w > breakpoints.desktopMid) return '1.15fr 1fr'
    if (w > breakpoints.desktopSmall) return '1.3fr 1fr'
    return '1.5fr 1fr'
  }

  const items = [
    t('action.items.01'),
    t('action.items.02'),
    t('action.items.03'),
    t('action.items.04'),
    t('action.items.05'),
    t('action.items.06'),
    t('action.items.07')
  ]

  const getContent = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: getGrid(),
        gap: stacked ? '2rem' : '3rem',
        // Uncropped, so it sits at the top of its column rather than being
        // stretched to the height of the copy.
        alignItems: 'start',
        width: '100%'
      }}
    >
      <Box>
        <SectionHeader
          eyebrow={t('action.eyebrow')}
          title={t('action.title')}
          intro={[t('action.intro1'), t('action.intro2')]}
        />

        <BulletGrid items={items} columns={stacked ? 2 : 1} />

        <Box
          sx={{
            marginTop: '2rem',
            paddingLeft: '1rem',
            borderLeft: `3px solid ${themeColors.success.main}`,
            maxWidth: '46rem'
          }}
        >
          {[t('action.outro1'), t('action.outro2')].map(paragraph => (
            <Typography
              key={paragraph}
              component="p"
              fontSize={getFontBody()}
              sx={{
                lineHeight: 1.7,
                color: themeColors.gray.defaultText,
                marginBottom: '0.5rem',
                '&:last-of-type': { marginBottom: 0 }
              }}
            >
              {paragraph}
            </Typography>
          ))}
        </Box>
      </Box>

      {/* No fixed height: the wrapper takes whatever the image needs, so the
          bottom of the photo is never clipped. */}
      <Box
        sx={{
          lineHeight: 0,
          order: stacked ? -1 : 0
        }}
      >
        <Image
          src={ActionImage}
          alt="Air quality event shown on the monitoring dashboard"
          sizes="(max-width: 700px) 100vw, 45vw"
          // Intrinsic ratio kept: the width scales, the height follows. It
          // never scales past its own pixel width, so it is not upsampled.
          style={{
            width: '100%',
            height: 'auto',
            maxWidth: ActionImage.width
          }}
        />
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.section.light}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
